// =============================================================================
// componentStatementPdf.ts — Plant & Subcontract Component Statement
// =============================================================================
// James, 28 Aug 2026: "with Ventia and some other clients they want to see
// subcontractor and plant invoices... we send the quoted amount as a
// standalone invoice... but I don't want to confuse Xero with invoices that
// are not really invoices — they're job deliverables for a client."
//
// So this generates exactly that deliverable: a document in Coretel's own
// invoice/quote design showing the QUOTED amounts for selected plant and
// subcontract lines, for the client to pass up the chain (Ventia → NBN).
//
// THE GUARDRAILS, deliberate and load-bearing:
//   * It is titled "PLANT & SUBCONTRACT COMPONENT", never "Tax Invoice" — a
//     second tax invoice for amounts already inside a progress claim would be
//     a duplicate document for one supply (GST confusion, double-processing
//     risk, unrecorded paper with our ABN).
//   * It always references the REAL claim invoice it belongs to, on its face.
//   * It shows Coretel's contracted sell amounts only. Actual supplier costs
//     never appear here — the margin between quoted plant and actual cost is
//     Coretel's business (that's where the margin lives; labour is deliberately
//     under-quoted to win the work).
//   * Xero is never touched. This is a project deliverable, not a receivable.
// =============================================================================

import {
  getPdfMake, fetchLogoDataUrl, PDF_COLOR as C, PDF_COMPANY as CO,
} from "@/features/quotes/quotePdf";

export interface ComponentStatementLine {
  source: string;              // "Q-26-0006-R3" or "VAR-26-0016" — where the amount was contracted
  code: string | null;         // rate line code, e.g. CRN-25T-FRANNA-OT
  description: string;
  detail: string | null;       // secondary description / breakdown
  quantity: number;
  unit: string;                // already label-ised by the caller ("hr", "LS", ...)
  rate_ex_gst: number;
  amount_ex_gst: number;
}

export interface ComponentStatementOpts {
  projectCode: string;
  projectName: string;
  siteName: string | null;
  clientName: string;
  refInvoiceNumber: string;    // the REAL claim this component belongs to
  refInvoiceDate: string | null;
  lines: ComponentStatementLine[];
  note?: string | null;        // optional free text (e.g. VAR context)
  preparedByName: string;
}

const money = new Intl.NumberFormat("en-AU", {
  style: "currency", currency: "AUD", minimumFractionDigits: 2, maximumFractionDigits: 2,
});
const m = (n: number) => money.format(Number(n || 0));
const d = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" }) : "—";

export async function generateComponentStatementPdf(opts: ComponentStatementOpts): Promise<void> {
  const pdfMake = await getPdfMake();
  const logo = await fetchLogoDataUrl("/coretel-logo-full.png");

  const total = opts.lines.reduce((a, l) => a + Number(l.amount_ex_gst || 0), 0);
  const today = new Date().toLocaleDateString("en-AU", { day: "2-digit", month: "long", year: "numeric" });

  const lineRows = opts.lines.map((l, i) => ([
    { text: String(i + 1), color: C.dim, fontSize: 8, margin: [0, 4, 0, 0] },
    {
      stack: [
        {
          text: [
            l.code ? { text: `${l.code}  `, color: C.primary, fontSize: 7.5, bold: true } : null,
            { text: l.description, bold: true, fontSize: 9.5, color: C.text },
          ].filter(Boolean),
        },
        l.detail ? { text: l.detail, fontSize: 8, color: C.muted, margin: [0, 1.5, 0, 0] } : null,
        { text: `Contracted under ${l.source}`, fontSize: 7.5, color: C.dim, margin: [0, 1.5, 0, 0] },
      ].filter(Boolean),
      margin: [0, 3, 0, 3],
    },
    { text: String(l.quantity), alignment: "right", fontSize: 9, margin: [0, 4, 0, 0] },
    { text: l.unit, color: C.muted, fontSize: 8.5, margin: [0, 4, 0, 0] },
    { text: m(l.rate_ex_gst), alignment: "right", fontSize: 9, margin: [0, 4, 0, 0] },
    { text: m(l.amount_ex_gst), alignment: "right", fontSize: 9.5, bold: true, margin: [0, 4, 0, 0] },
  ]));

  const doc: any = {
    pageSize: "A4",
    pageMargins: [45, 55, 45, 60],
    footer: (page: number, pages: number) => ({
      columns: [
        { text: `${CO.name}  ·  ABN ${CO.abn}`, fontSize: 7.5, color: C.dim },
        { text: `Component of ${opts.refInvoiceNumber}  ·  ${today}`, fontSize: 7.5, color: C.dim, alignment: "center" },
        { text: `Page ${page} of ${pages}`, fontSize: 7.5, color: C.dim, alignment: "right" },
      ],
      margin: [45, 20, 45, 0],
    }),
    content: [
      // ---- Header: logo + banner, mirrors the quote PDF -------------------
      {
        columns: [
          logo ? { image: logo, width: 120 } : { text: CO.name, fontSize: 14, bold: true, color: C.primary },
          {
            text: "PLANT & SUBCONTRACT\nCOMPONENT",
            alignment: "right", fontSize: 15, bold: true, color: C.primary,
            characterSpacing: 1.5, margin: [0, 6, 0, 0],
          },
        ],
      },
      { text: opts.projectCode ? `${opts.projectCode} · ${opts.projectName}` : opts.projectName,
        fontSize: 17, bold: true, color: C.ink, margin: [0, 26, 0, 2] },
      opts.siteName ? { text: opts.siteName, fontSize: 10.5, color: C.muted, margin: [0, 0, 0, 0] } : null,
      { canvas: [{ type: "line", x1: 0, y1: 0, x2: 60, y2: 0, lineWidth: 2, lineColor: C.primary }], margin: [0, 8, 0, 18] },

      // ---- Parties + reference -------------------------------------------
      {
        columns: [
          {
            width: "*",
            stack: [
              { text: "PREPARED FOR", fontSize: 7.5, bold: true, color: C.primary, characterSpacing: 1, margin: [0, 0, 0, 6] },
              { text: opts.clientName, fontSize: 10, color: C.text },
            ],
          },
          {
            width: "*",
            stack: [
              { text: "FORMS PART OF", fontSize: 7.5, bold: true, color: C.primary, characterSpacing: 1, margin: [0, 0, 0, 6] },
              { text: `Tax Invoice ${opts.refInvoiceNumber}`, fontSize: 10, bold: true, color: C.text },
              { text: `dated ${d(opts.refInvoiceDate)}`, fontSize: 9, color: C.muted, margin: [0, 2, 0, 0] },
            ],
          },
        ],
        columnGap: 24,
        margin: [0, 0, 0, 16],
      },

      // ---- The plain-English framing panel — the guardrail on the page ----
      {
        table: { widths: ["*"], body: [[{
          text: "This statement itemises the plant and subcontract component of the works at the amounts " +
                "contracted with the client, and is provided for client records and upstream substantiation. " +
                `It forms part of Tax Invoice ${opts.refInvoiceNumber} and is not a separate tax invoice — ` +
                "GST and payment are carried by the referenced invoice.",
          fontSize: 8.5, color: C.muted, margin: [10, 8, 10, 8],
        }]] },
        layout: "noBorders",
        fillColor: C.primaryWash,
        margin: [0, 0, 0, 18],
      },

      // ---- Lines ----------------------------------------------------------
      {
        table: {
          headerRows: 1,
          widths: [16, "*", 28, 28, 62, 70],
          body: [
            [
              { text: "#", color: "#ffffff", fontSize: 8, bold: true, margin: [0, 3, 0, 3] },
              { text: "Item", color: "#ffffff", fontSize: 8, bold: true, margin: [0, 3, 0, 3] },
              { text: "Qty", color: "#ffffff", fontSize: 8, bold: true, alignment: "right", margin: [0, 3, 0, 3] },
              { text: "Unit", color: "#ffffff", fontSize: 8, bold: true, margin: [0, 3, 0, 3] },
              { text: "Rate Ex GST", color: "#ffffff", fontSize: 8, bold: true, alignment: "right", margin: [0, 3, 0, 3] },
              { text: "Amount Ex GST", color: "#ffffff", fontSize: 8, bold: true, alignment: "right", margin: [0, 3, 0, 3] },
            ],
            ...lineRows,
          ],
        },
        layout: {
          fillColor: (row: number) => row === 0 ? C.primary : (row % 2 === 0 ? C.rowAlt : null),
          hLineWidth: (i: number, node: any) => (i === 0 || i === node.table.body.length) ? 0.75 : 0.5,
          vLineWidth: () => 0,
          hLineColor: () => C.rule,
          paddingLeft: () => 6, paddingRight: () => 6,
        },
      },

      // ---- Total ----------------------------------------------------------
      {
        columns: [
          { width: "*", text: "" },
          {
            width: 240,
            table: { widths: ["*", 90], body: [[
              { text: "COMPONENT TOTAL EX GST", fontSize: 9, bold: true, color: C.primary, characterSpacing: 0.5, margin: [8, 7, 0, 7] },
              { text: m(total), fontSize: 12, bold: true, color: C.primary, alignment: "right", margin: [0, 5, 8, 5] },
            ]] },
            layout: "noBorders",
            fillColor: C.primaryWash,
          },
        ],
        margin: [0, 14, 0, 0],
      },

      opts.note ? { text: opts.note, fontSize: 8.5, color: C.muted, margin: [0, 16, 0, 0] } : null,

      {
        text: `Prepared by ${opts.preparedByName}, ${CO.name} · ${today}`,
        fontSize: 8.5, color: C.dim, margin: [0, 22, 0, 0],
      },
    ].filter(Boolean),
  };

  const fileName = `${opts.projectCode || "project"}-plant-subcontract-component-${new Date().toISOString().slice(0, 10)}.pdf`;
  pdfMake.createPdf(doc).download(fileName);
}
